import { supabase } from '../lib/supabase';

const professionalBookingService = {
  /**
   * Create a new professional booking
   */
  async createBooking(bookingData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      const payload = {
        service_type: bookingData?.serviceType,
        transaction_type: bookingData?.transactionType,
        property_id: bookingData?.propertyId || null,
        client_name: bookingData?.clientName,
        client_email: bookingData?.clientEmail,
        client_phone: bookingData?.clientPhone || null,
        preferred_date: bookingData?.preferredDate,
        preferred_time: bookingData?.preferredTime || null,
        address: bookingData?.address || null,
        description: bookingData?.description || null,
        budget_estimate: bookingData?.budgetEstimate || null,
        notes: bookingData?.notes || null,
        booking_status: 'pending',
        professional_id: bookingData?.professionalId || null,
        professional_phone: bookingData?.professionalPhone || null,
      };

      if (user) {
        payload.user_id = user?.id;
      }

      const { data, error } = await supabase?.from('professional_bookings')?.insert(payload)?.select()?.single();

      if (error) throw error;

      // Trigger SMS notifications: notify professional + schedule client reminder
      if (data?.id) {
        professionalBookingService?._triggerSmsNotifications(data, bookingData)?.catch((err) => {
          console.warn('[ProfessionalBooking] SMS notification failed (non-blocking):', err);
        });
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating professional booking:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Trigger SMS notifications via edge function (non-blocking, fire-and-forget)
   * - Sends SMS to professional on new booking
   * - Schedules SMS reminder to client before appointment
   */
  async _triggerSmsNotifications(booking, bookingData) {
    try {
      const professionalPhone = bookingData?.professionalPhone || booking?.professional_phone || null;
      const professionalName = bookingData?.professionalName || booking?.professional_name || null;

      // If professional_id is provided but no phone, fetch it from professionals table
      let resolvedProfessionalPhone = professionalPhone;
      let resolvedProfessionalName = professionalName;

      if (!resolvedProfessionalPhone && (bookingData?.professionalId || booking?.professional_id)) {
        const { data: prof } = await supabase
          ?.from('professionals')
          ?.select('phone, whatsapp, full_name')
          ?.eq('id', bookingData?.professionalId || booking?.professional_id)
          ?.single();

        if (prof) {
          resolvedProfessionalPhone = prof?.phone || prof?.whatsapp || null;
          resolvedProfessionalName = prof?.full_name || resolvedProfessionalName;
        }
      }

      const { data: { session } } = await supabase?.auth?.getSession();
      const headers = { 'Content-Type': 'application/json' };
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session?.access_token}`;
      }

      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/send-professional-booking-sms`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            action: 'notify_professional',
            booking_id: booking?.id,
            professional_name: resolvedProfessionalName,
            professional_phone: resolvedProfessionalPhone,
            client_name: booking?.client_name,
            client_phone: booking?.client_phone,
            service_type: booking?.service_type,
            preferred_date: booking?.preferred_date,
            preferred_time: booking?.preferred_time,
            address: booking?.address,
          }),
        }
      );

      if (!response?.ok) {
        const errData = await response?.json()?.catch(() => ({}));
        console.warn('[ProfessionalBooking] SMS edge function error:', errData);
      } else {
        const result = await response?.json();
        console.log('[ProfessionalBooking] SMS notifications triggered:', result);
      }
    } catch (err) {
      console.warn('[ProfessionalBooking] SMS trigger error:', err);
    }
  },

  /**
   * Get bookings for the current authenticated user
   */
  async getUserBookings() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: [], error: null };

      const { data, error } = await supabase?.from('professional_bookings')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching user bookings:', error);
      return { data: [], error: error?.message };
    }
  },

  /**
   * Cancel a booking
   */
  async cancelBooking(bookingId) {
    try {
      const { data, error } = await supabase?.from('professional_bookings')?.update({ booking_status: 'cancelled' })?.eq('id', bookingId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error cancelling booking:', error);
      return { data: null, error: error?.message };
    }
  },
};

export default professionalBookingService;
